// src/encounter/device-trends/device-trends-format.ts
// Formatting and the ECharts option for DeviceTrendsCard (kept out of the component file so it exports only components).
import type { ComposeOption } from 'echarts/core';
import type { LineSeriesOption } from 'echarts/charts';
import type { GridComponentOption, TooltipComponentOption } from 'echarts/components';
import { clinicDateTimeD45 } from '@/shared/lib/clinic-time';
import type { WearableMetric, WearableSeries } from '../lib/wearable-trends';

type ChartOption = ComposeOption<LineSeriesOption | GridComponentOption | TooltipComponentOption>;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A calendar date (YYYY-MM-DD) is a day, not an instant: read it as that day. A server time without an offset is UTC. */
function parseDate(iso: string): Date {
  if (iso.length === 10) return new Date(`${iso}T00:00:00`);
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
}
export function formatDay(iso: string, withYear = false): string {
  const d = parseDate(iso);
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return withYear ? `${base} ${d.getFullYear()}` : base;
}
/** The last sync on the clinic's clock in D-45 ("31 Mar 2026, 10:00"). A server time without an offset is UTC. */
export function formatSync(iso: string): string {
  return clinicDateTimeD45(/(Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`) ?? '';
}

/**
 * The smallest y span a chart may draw, per metric (encounter owner sign-off m2, 1 Nov): an axis fitted to the data
 * makes 54-60 beats/min look like large swings. The span is centred on the data and never goes below zero.
 */
export const MIN_Y_SPAN: Readonly<Record<WearableMetric, number>> = {
  restingHeartRate: 20,
  hrvSdnn: 20,
  hrvRmssd: 20,
  sleepDuration: 4,
};

export function yRange(nums: number[], metric: WearableMetric, decimals: number): { min: number; max: number } {
  const f = 10 ** decimals;
  let lo = Math.min(...nums);
  let hi = Math.max(...nums);
  const span = MIN_Y_SPAN[metric];
  if (hi - lo < span) {
    const mid = (lo + hi) / 2;
    lo = mid - span / 2;
    hi = mid + span / 2;
    if (lo < 0) {
      hi -= lo;
      lo = 0;
    }
  }
  return { min: Math.floor(lo * f) / f, max: Math.ceil(hi * f) / f };
}
const decimalsFor = (unit: string) => (unit === 'h' ? 1 : 0);
export const formatValue = (unit: string, v: number) => v.toFixed(decimalsFor(unit));

/** The chart's colours and font, read from the showcase sheet's tokens on the chart element. */
export interface ChartTokens {
  dot: string;
  line: string;
  grid: string;
  meta: string;
  font: string;
}

/**
 * The ECharts option, as drawn in Claude Design round 3 on Antonin's design system: the wearable DAILY grammar — dots
 * joined only by a faint hairline, no area; a gap stays a gap; true 12 px labels in the meta ink; first, middle and last
 * dates on the x axis; three y labels. Every reading keeps a dot, so an isolated one never disappears (Codex review);
 * the most recent is larger. One neutral colour (clinical sign-off: no meaning colour, range or target).
 */
export function buildOption(s: WearableSeries, tok: ChartTokens, reduceMotion: boolean): ChartOption {
  const dates = s.points.map((p) => p.date);
  const values = s.points.map((p) => p.value);
  const latestIdx = dates.indexOf(s.latest.date);
  const dp = decimalsFor(s.unit);
  const nums = values.filter((v): v is number => v != null);
  const { min, max } = yRange(nums.length ? nums : [s.latest.value], s.metric, dp);
  const last = dates.length - 1;
  const label = { fontSize: 12, lineHeight: 16, color: tok.meta, fontFamily: tok.font };

  return {
    animation: !reduceMotion,
    grid: { left: 0, right: 8, top: 8, bottom: 0, containLabel: true },
    xAxis: {
      type: 'category',
      data: dates,
      boundaryGap: false,
      axisLine: { lineStyle: { color: tok.grid } },
      axisTick: { show: false },
      axisLabel: {
        ...label,
        interval: (i: number) => i === 0 || i === last || i === Math.round(last / 2),
        formatter: (d: string) => formatDay(d),
        hideOverlap: true,
      },
    },
    yAxis: {
      type: 'value',
      min,
      max,
      interval: (max - min) / 2,
      axisLabel: { ...label, formatter: (v: number) => (s.unit === 'h' ? `${v.toFixed(dp)} h` : v.toFixed(dp)) },
      splitLine: { lineStyle: { color: tok.grid } },
    },
    series: [
      {
        type: 'line',
        data: values,
        // A day without a value is a gap, never a line drawn across it (ruling, 30 Oct).
        connectNulls: false,
        showSymbol: true,
        // ECharts' default 'auto' thins symbols to the axis label interval at narrow widths, which can hide a lone
        // reading entirely (Codex review): every reading draws its dot.
        showAllSymbol: true,
        symbol: 'circle',
        symbolSize: (_v: unknown, p: { dataIndex: number }) => (p.dataIndex === latestIdx ? 7 : 4),
        itemStyle: { color: tok.dot, borderWidth: 0 },
        lineStyle: { color: tok.line, width: 1 },
        emphasis: { disabled: true },
        silent: true,
      },
    ],
  };
}


